import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Tooltip } from '@mui/material';
import { DataGridPremiumProps, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import DataGridCell from 'components/enhanced/data-grid/components/cell/DataGridCell';
import {
  filterOperators,
  singleSelectFilterOperators
} from 'components/enhanced/data-grid/utils/filters';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { IsTenantCoordinatorOrAdmin } from 'shared/permissions/IsTenantCoordinator';
import { LinkButton } from 'components/ui';
import { ProductWarehouseStatus } from 'features/inventory/products/constants';
import TenantPage from 'layouts/TenantPage';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusOption } from 'shared/types';
import { stockQuantityService } from '../api/stock-quantity.service';
import { StockQuantitiesDetail } from '../components/StockQuantityDetailPanel';
import { baseCell } from 'components/cells/BaseCell';
import { StockQuantitiesColumns } from '../stock-quantities';

export const StockQuantities = () => {
  const translationPrefix = 'pages.stock_quantity.list';

  const { t } = useTranslation();
  const datagridRefresh = useRef(null);
  const statusOptions: StatusOption[] = Object.entries(ProductWarehouseStatus).map(
    ([display_name, key]) => {
      return {
        value: Number(key),
        display_name: String(display_name)
      };
    }
  );

  const aggregatedColumns: GridColDef[] = [
    {
      field: 'product',
      valueGetter: (params) => params.row.product__code,
      headerName: t(`global.product`),
      width: 180,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      width: 120,
      type: 'singleSelect',
      filterOperators: singleSelectFilterOperators,
      valueOptions: statusOptions.map((opt) => {
        return {
          value: String(opt.value),
          label: opt.display_name
        };
      }),
      renderCell: ({ value }) => (
        <span className={value === 'ACTIVE' ? 'text-green-500' : 'text-orange-500'}>
          {t(`entities.product.status_options.${value}`)}
        </span>
      )
    },
    {
      field: 'product_type',
      valueGetter: (params) => params.row.product_type,
      headerName: t(`global.product_type`),
      width: 130,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'notes',
      headerName: t(`global.notes`),
      minWidth: 100,
      flex: 0,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'unit_price',
      type: 'string',
      headerName: t(`entities.movement.unit_price`),
      filterable: false,
      sortable: false,
      width: 100,
      renderCell: (params: GridRenderCellParams<any>) => (
        <IsTenantCoordinatorOrAdmin>
          <DataGridCell content={currencyFormatter(params.value)} />
        </IsTenantCoordinatorOrAdmin>
      )
    },
    {
      field: 'total_price',
      type: 'string',
      headerName: t(`global.total_price`),
      filterable: false,
      sortable: false,
      width: 100,
      valueGetter: (params) =>
        isNaN(params.row.unit_price)
          ? params.row.unit_price
          : params.row.unit_price * params.row.total_quantity,
      renderCell: (params: GridRenderCellParams<any>) => (
        <IsTenantCoordinatorOrAdmin>
          <DataGridCell content={currencyFormatter(params.value)} />
        </IsTenantCoordinatorOrAdmin>
      )
    },
    {
      field: 'category',
      headerName: t(`global.category`),
      width: 100,
      filterable: true,
      filterOperators,
      sortable: true,
      valueGetter: (params) => params.row.category_code ?? params.row.category,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      width: 300,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'manufacturer_name',
      valueGetter: (params) => params.row.manufacturer_name,
      headerName: t(`entities.product.manufacturer_name`),
      width: 220,
      filterOperators,
      sortable: true,
      renderCell: baseCell
    },
    {
      field: 'manufacturer_id',
      valueGetter: (params) => params.row.manufacturer_id,
      headerName: t(`entities.product.manufacturer_id`),
      width: 160,
      filterOperators,
      sortable: true,
      renderCell: baseCell
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      width: 250,
      filterOperators,
      valueGetter: (params) => params.row.warehouse__code,
      renderCell: baseCell
    },
    {
      field: 'locations',
      headerName: t(`global.locations`),
      minWidth: 100,
      flex: 0,
      filterOperators,
      valueGetter: (params) => {
        const locs = params.row.locations;
        return Array.isArray(locs) ? locs.join(', ') : locs ?? '';
      },
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 250,
      filterable: true,
      sortable: true,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'total_quantity',
      headerName: t(`entities.stock_quantity.total_quantity`),
      headerAlign: 'left',
      align: 'center',
      filterable: false,
      sortable: false,
      width: 100,
      type: 'number',
      renderCell: (params: GridRenderCellParams<any>) => {
        const understock = params.row.understock;
        const overstock = params.row.overstock;

        return (
          <div>
            {params.value}
            {understock !== null && params.value < understock && (
              <Tooltip title={t('alerts.understock', { value: understock })} placement="top">
                <TrendingDownIcon color="error" />
              </Tooltip>
            )}
            {overstock !== null && params.value > overstock && (
              <Tooltip title={t('alerts.overstock', { value: overstock })} placement="top">
                <TrendingUpIcon color="warning" />
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      field: 'total_quantity_ordered',
      headerName: t(`entities.stock_quantity.total_quantity_ordered`),
      width: 100,
      headerAlign: 'left',
      align: 'center',
      type: 'number',
      filterable: false,
      sortable: false,
      renderCell: baseCell
    }
  ];

  const columns: GridColDef[] = StockQuantitiesColumns(true);

  // const totalsOuterParams = useMemo(() => {
  //   return {
  //     quantity__gt: '0'
  //   };
  // }, []);

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridPremiumProps['getDetailPanelContent']>
  >(({ row }) => <StockQuantitiesDetail row={{ ...row }} columns={columns} />, [columns]);

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      menuRight={
        <>
          {/* 08/04/2025: Stefano ha chiesto di nascondere le "giacenze passate", confondevano gli operatori */}
          {/* <NavLink to="past">
              <Button>{t(`${translationPrefix}.past`)}</Button>
            </NavLink> */}
          <LinkButton label={t(`${translationPrefix}.all_quantities`)} to="all" />
        </>
      }>
      <EnhancedDataGrid
        columns={aggregatedColumns}
        service={stockQuantityService}
        getMethod={stockQuantityService.getTotalsByProduct}
        // outerParams={totalsOuterParams}
        warehouseFilterParam="warehouse"
        refresh={datagridRefresh}
        getDetailPanelContent={getDetailPanelContent}
      />
    </TenantPage>
  );
};

export default StockQuantities;
